import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Search, Eye, Edit, ChevronRight, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useEmpContratos, EmpContrato, getStatusLabel, STATUS_OPTIONS } from '@/hooks/useEmpContratos';
import { useEmpFeatureFlag } from '@/hooks/useEmpFeatureFlag';
import { useClusters } from '@/hooks/useClusters';
import { EmpContratoDialog } from './EmpContratoDialog';
import { RegistryStateChip } from '@/components/system/RegistryStateChip';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface EmpContratosListagemProps {
  onSelectContrato?: (contrato: EmpContrato) => void;
}

const statusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'ativo': return 'default';
    case 'rascunho': return 'outline';
    case 'encerrado': return 'secondary';
    case 'cancelado': case 'suspenso': return 'destructive';
    default: return 'outline';
  }
};

import { fmtCurrency as formatCurrency } from '@/lib/formatters';

const formatDate = (d: string | null) => {
  if (!d) return '—';
  try { return format(new Date(d + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR }); }
  catch { return d; }
};

interface HealthIndicator {
  spi: number | null;
  cpi: number | null;
}

function getSpiHealth(spi: number | null): { color: string; label: string; icon: typeof TrendingUp } {
  if (spi == null || spi === 0) return { color: 'text-muted-foreground', label: 'Sem dados', icon: Minus };
  if (spi >= 0.95) return { color: 'text-green-600', label: `SPI ${spi.toFixed(2)} — No prazo`, icon: TrendingUp };
  if (spi >= 0.85) return { color: 'text-yellow-600', label: `SPI ${spi.toFixed(2)} — Atenção`, icon: Minus };
  return { color: 'text-red-600', label: `SPI ${spi.toFixed(2)} — Atrasado`, icon: TrendingDown };
}

function useContractHealthMap(contratoIds: string[]) {
  return useQuery({
    queryKey: ['emp-health-indicators', ...contratoIds],
    queryFn: async () => {
      if (contratoIds.length === 0) return {} as Record<string, HealthIndicator>;
      // Get latest indicator per contract
      const { data, error } = await supabase
        .from('emp_indicadores' as any)
        .select('contrato_id, spi, cpi, periodo_referencia')
        .in('contrato_id', contratoIds)
        .order('periodo_referencia', { ascending: false });
      if (error) return {} as Record<string, HealthIndicator>;
      const map: Record<string, HealthIndicator> = {};
      for (const row of (data || []) as any[]) {
        if (!map[row.contrato_id]) {
          map[row.contrato_id] = { spi: row.spi, cpi: row.cpi };
        }
      }
      return map;
    },
    enabled: contratoIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });
}

export const EmpContratosListagem: React.FC<EmpContratosListagemProps> = ({ onSelectContrato }) => {
  const navigate = useNavigate();
  const { contratos, isLoading, canEdit } = useEmpContratos();
  const { activeClusterIds } = useEmpFeatureFlag();
  const { clusters } = useClusters();
  const activeClusters = clusters.filter(c => activeClusterIds.includes(c.id));

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clusterFilter, setClusterFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editContrato, setEditContrato] = useState<EmpContrato | null>(null);

  const filtered = useMemo(() => {
    return contratos.filter(c => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (clusterFilter !== 'all' && c.cluster_id !== clusterFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return c.nome.toLowerCase().includes(s) || c.numero_contrato.toLowerCase().includes(s) || c.contratada.toLowerCase().includes(s);
      }
      return true;
    });
  }, [contratos, search, statusFilter, clusterFilter]);

  const contratoIds = useMemo(() => filtered.map(c => c.id), [filtered]);
  const { data: healthMap = {} } = useContractHealthMap(contratoIds);

  const clusterName = (id: string) => clusters.find(c => c.id === id)?.name || '—';

  return (
    <div className="space-y-4 h-full">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
        <div className="relative flex-1 min-w-0 basis-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar contrato..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-auto min-w-[130px] max-w-[180px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos Status</SelectItem>
            {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {activeClusters.length > 1 && (
          <Select value={clusterFilter} onValueChange={setClusterFilter}>
            <SelectTrigger className="w-auto min-w-[130px] max-w-[200px]"><SelectValue placeholder="Cluster" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Clusters</SelectItem>
              {activeClusters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {canEdit && (
          <Button onClick={() => { setEditContrato(null); setDialogOpen(true); }} className="bg-emp-primary hover:bg-emp-primary/90 text-white whitespace-nowrap">
            <Plus className="h-4 w-4 mr-1" /> Novo Contrato
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="emp-card overflow-hidden min-w-0">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-lg font-medium">Nenhum contrato encontrado</p>
              <p className="text-sm mt-1">
                {contratos.length === 0
                  ? canEdit ? 'Crie o primeiro contrato de empreendimento.' : 'Nenhum contrato cadastrado.'
                  : 'Ajuste os filtros para ver mais resultados.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">Saúde</TableHead>
                  <TableHead>Nº Contrato</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Contratada</TableHead>
                  <TableHead>Cluster</TableHead>
                  <TableHead className="text-right">Valor Global</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[110px]">Governança</TableHead>
                  <TableHead>Versão</TableHead>
                   <TableHead className="text-right">Ações</TableHead>
                   <TableHead className="w-8"></TableHead>
                 </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(c => {
                  const health = healthMap[c.id];
                  const spiInfo = getSpiHealth(health?.spi ?? null);
                  const SpiIcon = spiInfo.icon;
                  return (
                  <TableRow key={c.id} className="cursor-pointer hover:bg-muted/40 transition-colors group" onClick={() => { onSelectContrato?.(c); navigate(`/empreendimentos/contratos/${c.id}`); }} title="Clique para ver detalhes do contrato">
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <SpiIcon className={`h-4 w-4 ${spiInfo.color}`} />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p className="text-xs font-medium">{spiInfo.label}</p>
                            {health?.cpi != null && health.cpi > 0 && (
                              <p className="text-xs text-muted-foreground">CPI {health.cpi.toFixed(2)}</p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="font-medium">{c.numero_contrato}</TableCell>
                    <TableCell>{c.nome}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.contratada}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{clusterName(c.cluster_id)}</Badge></TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(c.valor_global)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(c.data_inicio)} — {formatDate(c.data_fim_prevista)}</TableCell>
                    <TableCell><Badge variant={statusVariant(c.status)}>{getStatusLabel(c.status)}</Badge></TableCell>
                    <TableCell><RegistryStateChip record={c} domain="contrato" /></TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="outline">
                              {(c as any).planilha_versao_vigente ? `v${(c as any).planilha_versao_vigente}` : '—'}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p className="text-xs">
                              {`Planilha v${(c as any).planilha_versao_vigente ?? '—'} · Contrato v${c.versao}${c.versao === 1 ? ' (sem aditivos)' : ''}`}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" onClick={() => { onSelectContrato?.(c); navigate(`/empreendimentos/contratos/${c.id}`); }}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {canEdit && (
                          <Button variant="ghost" size="icon" onClick={() => { setEditContrato(c); setDialogOpen(true); }}>
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right w-8 pr-3">
                      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </div>

      <EmpContratoDialog open={dialogOpen} onOpenChange={setDialogOpen} editContrato={editContrato} />
    </div>
  );
};
